// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useListContacts from "../../../hooks/contacts/useListContacts.hook";

// ─── Custom dropdown ──────────────────────────────────────────────────────────
const ContactSelect = ({contacts, value, onChange, placeholder}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = contacts.find(c => c.contact_id === value);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{position: "relative", width: "100%", maxWidth: 480}}>
      {/* Trigger */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") setOpen(o => !o); }}
        style={{
          height: 48,
          borderRadius: open ? "24px 24px 0 0" : 999,
          border: open ? "1.5px solid #d732a8" : "1.5px solid rgba(215,50,168,0.35)",
          borderBottom: open ? "1.5px solid rgba(215,50,168,0.15)" : "1.5px solid rgba(215,50,168,0.35)",
          background: open ? "rgba(215,50,168,0.1)" : "rgba(215,50,168,0.06)",
          color: selected ? "#fff" : "rgba(255,255,255,0.4)",
          fontSize: 14,
          fontFamily: "inherit",
          padding: "0 44px 0 20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          userSelect: "none",
          boxShadow: open ? "0 0 0 2px rgba(215,50,168,0.3)" : "none",
          transition: "border-color 0.2s, background 0.2s, box-shadow 0.2s, border-radius 0.1s",
        }}
      >
        <span style={{flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
          {selected ? selected.name : placeholder}
        </span>
        <svg
          style={{position: "absolute", right: 16, top: "50%", transform: open ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)", transition: "transform 0.2s"}}
          width="14" height="14" viewBox="0 0 14 14" fill="none"
        >
          <path d="M3 5l4 4 4-4" stroke="#d732a8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Options panel */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#0f0f1a",
            border: "1.5px solid #d732a8",
            borderTop: "none",
            borderRadius: "0 0 20px 20px",
            overflow: "hidden",
            zIndex: 200,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          {contacts.map((contact, idx) => {
            const isSelected = contact.contact_id === value;
            return (
              <OptionRow
                key={contact.contact_id}
                label={contact.name}
                isSelected={isSelected}
                isLast={idx === contacts.length - 1}
                onClick={() => {
                  onChange(contact.contact_id);
                  setOpen(false);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const OptionRow = ({label, isSelected, isLast, onClick}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === "Enter") onClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "11px 20px",
        fontSize: 14,
        fontFamily: "inherit",
        cursor: "pointer",
        color: isSelected ? "#fff" : "rgba(255,255,255,0.75)",
        background: isSelected
          ? "linear-gradient(135deg,rgba(250,84,0,0.25),rgba(215,50,168,0.25))"
          : hovered
            ? "rgba(215,50,168,0.12)"
            : "transparent",
        borderBottom: isLast ? "none" : "1px solid rgba(215,50,168,0.1)",
        fontWeight: isSelected ? 700 : 400,
        transition: "background 0.15s, color 0.15s",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {label}
      {isSelected && (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2 7l4 4 6-7" stroke="#d732a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const TransferUserId = ({selectedContact, setSelectedContact, setIsNextDisabled}) => {
  const {contacts, isSuccess, isError} = useListContacts();
  const {t} = useTranslation();

  const selectValue = selectedContact?.id ?? "";

  useEffect(() => {
    if (!selectedContact?.id) setIsNextDisabled?.(true);
  }, [selectedContact, setIsNextDisabled]);

  const handleChange = contactId => {
    if (!contactId) {
      setSelectedContact(null);
      setIsNextDisabled?.(true);
      return;
    }
    const contact = contacts.find(c => c.contact_id === contactId);
    setSelectedContact({id: contact.contact_id, name: contact.name});
    setIsNextDisabled?.(false);
  };

  if (isError) return <div style={{color: "var(--color-danger)"}}>{t("transferSteps_loadingError")}</div>;
  if (!isSuccess) return <div style={{color: "var(--color-text-secondary)"}}>{t("transferSteps_loading")}</div>;

  return (
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 16, paddingTop: 8, paddingBottom: 16, width: "100%"}}>
      <h2 style={{fontWeight: 700, fontSize: 16, margin: 0, color: "#fff"}}>
        {t("transferStepsSelectContact")}:
      </h2>
      <ContactSelect
        contacts={contacts}
        value={selectValue}
        onChange={handleChange}
        placeholder={t("transferStepsSelectContactOption")}
      />
    </div>
  );
};

TransferUserId.propTypes = {
  selectedContact: PropTypes.object,
  setSelectedContact: PropTypes.func.isRequired,
  setIsNextDisabled: PropTypes.func,
};

export default TransferUserId;
